import React, { useState } from "react";

import { FormProvider, useForm } from "react-hook-form";

import { createEmptyBulletinForm } from "@/components/alerts/add-v2/model/defaults";
import { BulletinFormValues } from "@/components/alerts/add-v2/model/types";
import { BulletinRowProvider } from "@/components/alerts/add-v2/persistence/BulletinRowProvider";
import { useBulletinResolver } from "@/components/alerts/add-v2/validation/useBulletinSchema";

import { BulletinDragProvider } from "./BulletinDragProvider";

interface BulletinFormProviderProps {
  initialValues?: BulletinFormValues;
  /** The bulletin these values were loaded from, when they were loaded from one. */
  bulletinId?: number;
  children: React.ReactNode;
}

/**
 * Form state for the whole page — one `useForm`, per README decision 8.
 *
 * There is no submit handler. Saving is autosave through the repository and finalizing is a
 * repository call too, so this form is never submitted; react-hook-form is here for values, touched
 * state and validation only.
 *
 * `mode: "onChange"` is what puts an error on a field as soon as the value in it becomes wrong.
 * It is affordable because nothing subscribes to the whole document except the page shell and the
 * preview — see `bulletinFormContext`.
 *
 * What it does **not** drive is the "N required fields left" count. With a resolver,
 * react-hook-form writes an error only for the field that changed, so `formState.errors` describes
 * what each input should show rather than what the draft is still missing; the page runs the same
 * schema over the whole document instead. See `validation/sectionErrors.ts`.
 *
 * Default values are captured once. Phase 8's async sources do **not** need `reset()` because they
 * resolve before this mounts — see `hooks/useBulletinSource.ts` for why that is a safety property and
 * not just a convenience.
 *
 * `BulletinRowProvider` and `BulletinDragProvider` are nested here rather than in the page. For the
 * row that is a correctness property, below; for the drag context it is that the sections need it
 * and this is what "a bulletin form" already means.
 *
 * `BulletinRowProvider` is nested here rather than in the page so that one form instance means one
 * bulletin row: the media uploads create it and `save` reuses it. It holds no form state and
 * renders nothing. In edit mode `bulletinId` hands it the row that already exists, so the first
 * autosave PATCHes the bulletin the officer opened instead of creating a second one.
 *
 * react-hook-form clones what it is given here, which was worth checking because the form holds
 * `Dayjs` values and a clone that only special-cases `Date` would hand back plain objects whose
 * `toISOString()` throws. It does not: `phase2.test.tsx` pins `Dayjs` surviving both as an initial
 * value and through a write. Anything added to the model that is neither plain data nor a `Dayjs`
 * should be pinned the same way.
 */
export const BulletinFormProvider: React.FC<BulletinFormProviderProps> = ({
  initialValues,
  bulletinId,
  children,
}) => {
  const [defaultValues] = useState(
    () => initialValues ?? createEmptyBulletinForm(),
  );

  const resolver = useBulletinResolver();

  const form = useForm<BulletinFormValues>({
    defaultValues,
    mode: "onChange",
    resolver,
  });

  return (
    <FormProvider {...form}>
      <BulletinRowProvider existingId={bulletinId}>
        {/*
          Nested here for the same reason as the row holder: the sections require it. Two of them
          call `useDndMonitor`, which throws outside a `DndContext`, and one form is one drag
          context — a media card dropped on an entity card crosses between two sections, so the
          context cannot belong to either (decision 52).
        */}
        <BulletinDragProvider>{children}</BulletinDragProvider>
      </BulletinRowProvider>
    </FormProvider>
  );
};
